import { Fragment, useState } from "react";
import { Button, Card, EmptyState, Stack, useToast } from "@salesapp/ui";
import { crmApi, type QuoteVersionSummary } from "../../lib/crmApi";
import { opsApi } from "../../lib/opsApi";
import { useLocale } from "../../shell/LocaleProvider";

const REASON_LABELS: Record<QuoteVersionSummary["reason"], string> = {
  edited: "Edited",
  regenerated_from_opportunity: "Regenerated from opportunity",
  reverted: "Reverted to an earlier version",
};

export function QuoteVersionsPanel({
  orgId,
  quoteId,
  versions,
  currentVersionNumber,
  onActivated,
}: {
  orgId: string;
  quoteId: string;
  versions: QuoteVersionSummary[];
  currentVersionNumber: number | null;
  onActivated: () => void | Promise<void>;
}) {
  const { formatDateTime } = useLocale();
  const { show } = useToast();
  const [openVersion, setOpenVersion] = useState<number | null>(null);
  const [previewHtml, setPreviewHtml] = useState<string>("");
  const [loadingVersion, setLoadingVersion] = useState<number | null>(null);
  const [activating, setActivating] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function togglePreview(versionNumber: number) {
    if (openVersion === versionNumber) {
      setOpenVersion(null);
      return;
    }
    setError(null);
    setLoadingVersion(versionNumber);
    try {
      const { html } = await opsApi.previewQuoteVersion(orgId, quoteId, versionNumber);
      setPreviewHtml(html);
      setOpenVersion(versionNumber);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Version preview could not be loaded.");
    } finally {
      setLoadingVersion(null);
    }
  }

  async function activate(versionNumber: number) {
    setError(null);
    setActivating(versionNumber);
    try {
      await crmApi.activateQuoteVersion(quoteId, versionNumber, orgId);
      show("Quote reverted to this version’s content", "success");
      setOpenVersion(null);
      await onActivated();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not activate this version.");
    } finally {
      setActivating(null);
    }
  }

  if (versions.length === 0) {
    return (
      <EmptyState
        title="No versions yet"
        description="Every edit, and every time this quote is regenerated from its opportunity, is recorded here with what changed and when."
      />
    );
  }

  return (
    <div className="crm-table-card">
      <h2 className="crm-section-title">Quote versions</h2>
      <p className="crm-muted">
        {versions.length} {versions.length === 1 ? "version" : "versions"} · Newest first
      </p>
      {error && <p className="crm-error">{error}</p>}
      <table className="crm-table" aria-label="Quote versions">
        <thead>
          <tr>
            <th>Version</th>
            <th>Quote number</th>
            <th>Reason</th>
            <th>Modified</th>
            <th>By</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {versions.map((version) => (
            <Fragment key={version.version_number}>
              <tr>
                <td>{String(version.version_number).padStart(2, "0")}</td>
                <td>{version.quote_number}</td>
                <td>{REASON_LABELS[version.reason] ?? version.reason}</td>
                <td>{formatDateTime(version.created_at)}</td>
                <td>{version.created_by_user_id || "—"}</td>
                <td>
                  <Stack gap={1}>
                    <Button
                      variant="ghost"
                      size="sm"
                      disabled={loadingVersion === version.version_number}
                      onClick={() => togglePreview(version.version_number)}
                    >
                      {loadingVersion === version.version_number
                        ? "Loading…"
                        : openVersion === version.version_number
                          ? "Hide preview"
                          : "View"}
                    </Button>
                    {version.version_number !== currentVersionNumber && (
                      <Button
                        variant="secondary"
                        size="sm"
                        disabled={activating === version.version_number}
                        title="Make this version's content the current quote, as a new version."
                        onClick={() => activate(version.version_number)}
                      >
                        {activating === version.version_number ? "Activating…" : "Make current"}
                      </Button>
                    )}
                  </Stack>
                </td>
              </tr>
              {openVersion === version.version_number && (
                <tr>
                  <td colSpan={6}>
                    <Card>
                      <div className="crm-document-sheet" dangerouslySetInnerHTML={{ __html: previewHtml }} />
                    </Card>
                  </td>
                </tr>
              )}
            </Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
}
